import { useState, type ReactNode, type MouseEvent } from 'react'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { IconButton } from './IconButton'
import { useNls } from '../../nls/nlsProvider'
import './settingsKit.css'

export interface SettingsRowProps {
  label: ReactNode
  hint?: ReactNode
  control?: ReactNode
  status?: ReactNode
  disabled?: boolean
  stacked?: boolean
  onClick?: () => void
  className?: string
  children?: ReactNode
  testId?: string
}

/**
 * SettingsRow (SR2, §3.3, §3.7)
 * Grouped settings row. Label and hint on left, control on right.
 * Whole row is clickable for switches. Stacks under 560px via container query.
 */
export function SettingsRow({
  label,
  hint,
  control,
  status,
  disabled = false,
  stacked = false,
  onClick,
  className,
  children,
  testId,
}: SettingsRowProps) {
  const isClickable = Boolean(onClick) && !disabled

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled || !onClick) return
    const target = e.target as HTMLElement
    // Only skip if click originated inside the control area
    const controlEl = e.currentTarget.querySelector('.skRow__control')
    if (controlEl && controlEl.contains(target)) {
      return
    }
    onClick()
  }

  const contentControl = control ?? children

  return (
    <div
      className={[
        'skRow',
        isClickable ? 'is-clickable' : '',
        disabled ? 'is-disabled' : '',
        stacked ? 'skRow--stacked' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
      data-testid={testId}
    >
      <div className="skRow__main">
        <span className="skRow__label">{label}</span>
        {hint ? <small className="skRow__hint">{hint}</small> : null}
      </div>
      {contentControl || status ? (
        <div className="skRow__control">
          {status ? (
            <div className="skRow__status" aria-live="polite">
              {status}
            </div>
          ) : null}
          {contentControl}
        </div>
      ) : null}
    </div>
  )
}

export interface SettingsReadOnlyRowProps {
  label: ReactNode
  value: ReactNode
  hint?: ReactNode
  copyValue?: string
  chip?: ReactNode
  className?: string
  testId?: string
}

/**
 * SettingsReadOnlyRow (§3.3)
 * Static read-only row with label + value + optional copy button and status chip.
 */
export function SettingsReadOnlyRow({
  label,
  value,
  hint,
  copyValue,
  chip,
  className,
  testId,
}: SettingsReadOnlyRowProps) {
  const [copied, setCopied] = useState<boolean>(false)
  const { t } = useNls()

  const handleCopy = async () => {
    if (!copyValue) return
    try {
      await navigator.clipboard.writeText(copyValue)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback
    }
  }

  return (
    <SettingsRow
      label={label}
      hint={hint}
      className={['skReadOnlyRow', className].filter(Boolean).join(' ')}
      testId={testId}
      control={
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          {chip}
          <span className="skReadOnlyRow__value">{value}</span>
          {copyValue ? (
            <IconButton
              size="sm"
              label={
                copied
                  ? t('NLSSettingsKitCopied')
                  : t('NLSSettingsKitCopyValue')
              }
              icon={
                copied ? (
                  <CheckRoundedIcon fontSize="inherit" />
                ) : (
                  <ContentCopyRoundedIcon fontSize="inherit" />
                )
              }
              title={
                copied
                  ? t('NLSSettingsKitCopied')
                  : t('NLSSettingsKitCopyValue')
              }
              onClick={handleCopy}
              className="skReadOnlyRow__copyBtn"
            />
          ) : null}
        </div>
      }
    />
  )
}
